import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../Controllers/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../Controllers/Store";
import { hasPermission } from "../../Controllers/Selectors/permissionsSelector";

import { Ripple } from "primereact/ripple";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tooltip } from "primereact/tooltip";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Nullable } from "primereact/ts-helpers";
import { InputText } from "primereact/inputtext";
import { Dropdown, DropdownChangeEvent, DropdownProps } from "primereact/dropdown";
import { confirmDialog } from "primereact/confirmdialog";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";

import TabFilter from "../../Components/TabFilter";
import NoDataCreated from "../../Components/NoDataCreated";
import PageLoader from "../../Components/PageLoader";
import ErrorPage from "../../Components/ErrorPage";
import GoBackButton from "../../Components/GoBackButton";
import DataListModalInput from "../../Components/DataListModal/DataListModalInput";
import DataListModalSearch from "../../Components/DataListModal/DataListModalSearch";
import DataListModalClose from "../../Components/DataListModal/DataListModalClose";
import CustomTableLoader from "../../Components/CustomTableLoader";
import DataException from "../../Components/DataException";
import DatePicker from "../../Components/DatePicker";
import TableActionButton from "../../Components/TableActionButton";
import { goToTop } from "../../Components/goToTop";
import { checkModelSelected, checkText } from "../../Utils/Validator";
import { formatDate, parseStringToDate } from "../../Utils/getDate";

import { Project } from "../../Controllers/Types/Project/ProjectInterface";
import { ProjectProgressService } from "../../Controllers/Types/ProjectProgress/ProjectProgressService";
import { ProjectProgress as ProjectProgressType } from "../../Controllers/Types/ProjectProgress/ProjectProgress";
import { fetchProjectProgressData, fetchProjectProgressServiceData } from "../../Controllers/Thunks/projectProgressThunks";
import { fetchAllProjects } from "../../Controllers/Thunks/projectThunks";
import {
    createProjectProgressServiceAPI, deleteProjectProgressServiceAPI, updateProjectProgressServiceAPI
} from "../../Api/ProjectProgress/projectProgressServiceApi";
import {
    changeProjectProgressStatusAPI,
    changeProjectProgressStatusDecessionAPI,
    createProjectProgressAPI, deleteProjectProgressAPI, updateProjectProgressAPI
} from "../../Api/ProjectProgress/projectProgressApi";

const projectProgressTabs = ['All', 'Pending', 'Validated', 'In progress', 'Submitted'] as const;
const projectProgressStatuses = ['Pending', 'Validated', 'In progress', 'Submitted'];

const projectProgressStatusesDecession = ['Pending', 'Approved', 'Rejected',];

type ProjectTab = typeof projectProgressTabs[number];

const ProjectProgress: React.FC = () => {
    const dispatch = useAppDispatch();
    const toast = useRef<Toast>(null);
    const today = new Date();

    const [loadingState, setLoadingState] = useState<'Next' | 'Close' | null>(null);
    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);
    const [showProjectProgressModal, setShowProjectProgressModal] = useState<boolean>(false);
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [selectedProjectProgress, setSelectedProjectProgress] = useState<ProjectProgressType | null>(null);
    const [projectProgressUpdateHasChanges, setProjectProgressUpdateHasChanges] = useState<boolean>(false);
    const [customLoading, setCustomLoading] = useState<boolean>(false);
    const [customDataState, setCustomDataState] = useState<"Add" | "Edit">("Add");
    const [deleteProgressLoadingId, setDeleteProgressLoadingId] = useState<number | null>(null);
    const [showProjectProgressDetailModal, setShowProjectProgressDetailModal] = useState<boolean>(false);
    const [openMenuId, setOpenMenuId] = useState<number | null>(null);
    const menuRefs = useRef<Record<number, Menu | null>>({});
    const buttonRefs = useRef<Record<number, HTMLButtonElement | null>>({});


    // Filters
    const [activeTab, setActiveTab] = useState<ProjectTab>('All');
    const [jobDateFilter, setJobDateFilter] = useState<Nullable<Date>>(null);
    const [projectFilter, setProjectFilter] = useState<Project | null>(null);

    // State data
    const { allProjects: projects, loading: loadingProjects } = useAppSelector((state) => state.project);

    const {
        projectProgresses, loadingProjectProgress, errorProjectProgress, isFetchedProjectProgress,
        projectProgressServices, loadingProjectProgressService, errorProjectProgressService, isFetchedProjectProgressService
    } = useAppSelector((state: RootState) => state.projectProgress);

    // Form fields
    const [project, setProject] = useState<Project | null>(null);
    const [projectProgressService, setProjectProgressService] = useState<ProjectProgressService | null>(null);
    const [jobDate, setJobDate] = useState<Nullable<Date>>(today);
    const [jobSubmissionDate, setJobSubmissionDate] = useState<Nullable<Date>>(null);
    const [applicationSubmittedDate, setApplicationSubmittedDate] = useState<Nullable<Date>>(null);
    const [decisionDate, setDecisionDate] = useState<Nullable<Date>>(null);

    // Form errors
    const [projectError, setProjectError] = useState<string>('');
    const [projectProgressServiceError, setProjectProgressServiceError] = useState<string>('');
    const [jobDateError, setJobDateError] = useState<string>('');
    const [jobSubmissionDateError, setJobSubmissionDateError] = useState<string>('');
    const [applicationSubmittedDateError, setApplicationSubmittedDateError] = useState<string>('');
    const [decisionDateError, setDecisionDateError] = useState<string>('');

    // Project progress service
    const [showProjectProgressServiceModal, setShowProjectProgressServiceModal] = useState<boolean>(false);
    const [projectProgressServiceUpdateHasChanges, setProjectProgressServiceUpdateHasChanges] = useState<boolean>(false);
    const [selectedCustomProjectProgressService, setSelectedCustomProjectProgressService] = useState<ProjectProgressService | null>(null);
    const [projectProgressServiceSearchKey, setProjectProgressServiceSearchKey] = useState<string>('');
    const [customProjectProgressServiceName, setCustomProjectProgressServiceName] = useState<string>('');
    const [customProjectProgressServiceNameError, setCustomProjectProgressServiceNameError] = useState<string>('');
    const [deleteServiceLoadingId, setDeleteServiceLoadingId] = useState<number | null>(null);

    // Permissions
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const hasPermissionForAddProjectProgress = useCanEdit("Project Progress", "add_project_progress");
    const hasPermissionForEditProjectProgress = useCanEdit("Project Progress", "edit_project_progress");
    const hasPermissionForDeleteProjectProgress = useCanEdit("Project Progress", "delete_project_progress");
    const hasPermissionForChangeProjectProgressStatus = useCanEdit("Project Progress", "change_project_progress_status");
    const hasPermissionForAddProjectProgressService = useCanEdit("Project Progress", "add_project_progress_service");
    const hasPermissionForEditProjectProgressService = useCanEdit("Project Progress", "edit_project_progress_service");
    const hasPermissionForDeleteProjectProgressService = useCanEdit("Project Progress", "delete_project_progress_service");


    useEffect(() => {
        dispatch(fetchAllProjects());
        if (!isFetchedProjectProgress) handleGetProjectProgressData();
        if (!isFetchedProjectProgressService) dispatch(fetchProjectProgressServiceData());
    }, [isFetchedProjectProgress, isFetchedProjectProgressService, dispatch]);

    const filteredProjectProgressData = useMemo(() => {
        let filteredData = projectProgresses;

        if (jobDateFilter) {
            filteredData = filteredData.filter((e) => e.job_date === formatDate(jobDateFilter));
        }

        if (projectFilter) {
            filteredData = filteredData.filter((e) => e.project_id === projectFilter.project_id);
        }

        if (activeTab === 'All') return filteredData;
        if (activeTab === 'Pending') return filteredData.filter((e) => e.application_validated === 'Pending');
        if (activeTab === 'Validated') return filteredData.filter((e) => e.application_validated === 'Validated');
        if (activeTab === 'In progress') return filteredData.filter((e) => e.application_validated === 'In progress');
        if (activeTab === 'Submitted') return filteredData.filter((e) => e.application_validated === 'Submitted');

        return filteredData;
    }, [projectProgresses, activeTab, jobDateFilter, projectFilter]);

    const filteredProjectProgressServiceData = useMemo(() => {
        if (projectProgressServiceSearchKey === '') return projectProgressServices;
        return projectProgressServices.filter((e) => e.service_name.toLowerCase().includes(projectProgressServiceSearchKey.toLowerCase()));
    }, [projectProgressServices, projectProgressServiceSearchKey]);

    const handleGetProjectProgressData = async () => {
        await dispatch(fetchProjectProgressData());
        setInitialDataLoading(false);
    };

    const handleFilterProjectProgress = (filterState: ProjectTab) => {
        setActiveTab(filterState);
        goToTop();
    };

    const getEmptyMessage = () => {
        if (!projectProgresses || projectProgresses.length === 0) {
            return hasPermissionForAddProjectProgress
                ? 'No project progress found. Click the Add button to add project progress.'
                : 'No project progress found.';
        }

        if (activeTab === 'All') {
            if (jobDateFilter) {
                return 'No project progress have been added for the selected job date.';
            }
            if (projectFilter) {
                return 'No project progress have been added for the selected project.';
            }
            return 'No project progress have been added.';
        }

        if (activeTab === 'Pending') {
            return 'No pending project progress found.';
        }

        return 'No validated project progress found.';
    };


    /* **************************************************************** Project Progress */
    const handleCloseProjectProgressModal = (saveState: 'Next' | 'Close' | null) => {
        handleResetProjectProgressForm();
        handleResetProjectProgressFormErrors();
        setSelectedProjectProgress(null);
        setLoadingState(null);

        if (saveState === 'Close') {
            setShowProjectProgressModal(false);
        }
    };

    const handleResetProjectProgressForm = () => {
        setProject(null);
        setProjectProgressService(null);
        setJobDate(today);
        setJobSubmissionDate(null);
        setApplicationSubmittedDate(null);
        setDecisionDate(null);
    };

    const handleResetProjectProgressFormErrors = () => {
        setProjectError('');
        setProjectProgressServiceError('');
        setJobDateError('');
        setJobSubmissionDateError('');
        setApplicationSubmittedDateError('');
        setDecisionDateError('');
    };

    const selectedProjectTemplate = (option: Project, props: DropdownProps) => {
        if (option) {
            return (
                <span>
                    <b>{option?.job_number}</b>&nbsp;-&nbsp;{option?.address}
                </span>
            );
        }

        return <span>{props.placeholder}</span>;
    };

    const projectOptionTemplate = (option: Project) => {
        return (
            <div
                className={`address_option_container d-flex justify-content-between align-items-center ${option?.project_type === "Commercial" ? "bg-blue" : "bg-purple"}`}
            >
                <span>
                    <b>{option?.job_number}</b>:&nbsp;
                    {option?.address}
                </span>

                <Tooltip
                    target={`.project_${option?.project_id}_type`}
                    position="left"
                />
                <span className={`project_type_badge project_${option?.project_id}_type`} data-pr-tooltip={option?.project_type === 'Commercial' ? 'Commercial Project' : 'Domestic Project'}>
                    {option?.project_type === 'Commercial' ? 'CP' : 'DP'}
                </span>
            </div>
        );
    };

    // Clear errors
    useEffect(() => setProjectError(""), [project]);
    useEffect(() => setProjectProgressServiceError(""), [projectProgressService]);
    useEffect(() => setJobDateError(""), [jobDate]);
    useEffect(() => setJobSubmissionDateError(""), [jobSubmissionDate]);
    useEffect(() => setApplicationSubmittedDateError(""), [applicationSubmittedDate]);
    useEffect(() => setDecisionDateError(""), [decisionDate]);

    // Check vaidation
    function checkCredentialsForProjectProgress(): boolean {
        const isValidProject = checkModelSelected((project !== null), true, setProjectError);
        const isValidProjectProgressService = checkModelSelected(projectProgressService !== null, true, setProjectProgressServiceError);
        const isValidJobDate = checkText(formatDate(jobDate ?? null) ?? '', true, setJobDateError);
        const isValidJobSubmissionDate = checkText(formatDate(jobSubmissionDate ?? null) ?? '', false, setJobSubmissionDateError);
        const isValidApplicationSubmittedDate = checkText(formatDate(applicationSubmittedDate ?? null) ?? '', false, setApplicationSubmittedDateError);
        const isValidDecisionDate = checkText(formatDate(decisionDate ?? null) ?? '', false, setDecisionDateError);

        return (
            isValidProject &&
            isValidProjectProgressService &&
            isValidJobDate &&
            isValidJobSubmissionDate &&
            isValidApplicationSubmittedDate &&
            isValidDecisionDate
        );
    }

    // Check API Error
    function showAPIErrorForProjectProgress(keyName: string, error: string): void {
        switch (keyName) {
            case "project_id":
                setProjectError(error);
                break;
            case "service_id":
                setProjectProgressServiceError(error);
                break;
            case "job_date":
                setJobDateError(error);
                break;
            case "job_submission_date":
                setJobSubmissionDateError(error);
                break;
            case "application_submitted_date":
                setApplicationSubmittedDateError(error);
                break;
            case "decision_date":
                setDecisionDateError(error);
                break;
            default:
                break;
        }
    }
    /* **************************************************************** */


    /* **************************************************************** Add Project Progress */
    const handleAddProjectProgress = () => {
        setShowProjectProgressModal(true);
        setDataState('Add');
    };

    const handleCreateProjectProgress = async (e: React.FormEvent, saveState: 'Next' | 'Close') => {
        e.preventDefault();

        if (!checkCredentialsForProjectProgress()) return;

        setLoadingState(saveState);

        try {
            const response = await createProjectProgressAPI(
                project?.project_id ?? 0,
                projectProgressService?.id ?? 0,
                formatDate(jobDate ?? null) ?? '',
                formatDate(jobSubmissionDate ?? null) ?? null,
                formatDate(applicationSubmittedDate ?? null) ?? null,
                formatDate(decisionDate ?? null) ?? null,
            );

            if (response.statusCode === 200) {
                await handleGetProjectProgressData();
                handleCloseProjectProgressModal(saveState);

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Project progress created successfully.",
                        life: 3000,
                    });
                }
            } else if (response.statusCode === 422) {
                Object.entries(response.errors ?? []).map(([key, value], index) => (
                    showAPIErrorForProjectProgress(key, value[0])
                ));
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error creating the project progress.",
                        life: 3000,
                    });
                }
            }
        } catch (error) {
            console.error("Error adding the project progress:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while adding the project progress.",
                life: 3000,
            });
        } finally {
            setLoadingState(null);
        }
    }
    /* **************************************************************** */


    /* **************************************************************** Edit Project Progress */
    // Check update has Changes
    const hasChanges = useCallback(() => {
        if (!selectedProjectProgress) return false;

        return (
            selectedProjectProgress.project_id !== project?.project_id ||
            selectedProjectProgress.service_id !== projectProgressService?.id ||
            (selectedProjectProgress.job_date ?? '') !== (formatDate(jobDate ?? null) ?? '') ||
            (selectedProjectProgress.job_submission_date ?? '') !== (formatDate(jobSubmissionDate ?? null) ?? '') ||
            (selectedProjectProgress.application_submitted_date ?? '') !== (formatDate(applicationSubmittedDate ?? null) ?? '') ||
            (selectedProjectProgress.decision_date ?? '') !== (formatDate(decisionDate ?? null) ?? '')
        );
    }, [selectedProjectProgress, project, projectProgressService, jobDate, jobSubmissionDate, applicationSubmittedDate, decisionDate]);

    useEffect(() => {
        if (hasChanges()) {
            setProjectProgressUpdateHasChanges(true);
        } else {
            setProjectProgressUpdateHasChanges(false);
        }
    }, [hasChanges]);

    const handleEditProjectProgress = (projectProgress: ProjectProgressType) => {
        if (!projectProgress) return;

        setSelectedProjectProgress(projectProgress);
        setShowProjectProgressModal(true);
        setDataState('Edit');

        // Set form fields
        const existingProject = projects?.find((e) => e.project_id === projectProgress.project_id) ?? null;
        const existingProjectProgressService = projectProgressServices?.find((e) => e.id === projectProgress.service_id) ?? null;
        const existingJobDate = projectProgress.job_date ? parseStringToDate(projectProgress.job_date) : null;
        const existingJobSubmissionDate = projectProgress.job_submission_date ? parseStringToDate(projectProgress.job_submission_date) : null;
        const existingApplicationSubmittedDate = projectProgress.application_submitted_date ? parseStringToDate(projectProgress.application_submitted_date) : null;
        const existingDecisionDate = projectProgress.decision_date ? parseStringToDate(projectProgress.decision_date) : null;

        setProject(existingProject);
        setProjectProgressService(existingProjectProgressService);
        setJobDate(existingJobDate);
        setJobSubmissionDate(existingJobSubmissionDate);
        setApplicationSubmittedDate(existingApplicationSubmittedDate);
        setDecisionDate(existingDecisionDate);
    };

    const handleUpdateProjectProgress = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForProjectProgress() || !selectedProjectProgress) return;

        setLoadingState('Close');

        try {
            const response = await updateProjectProgressAPI(
                selectedProjectProgress.id,
                project?.project_id ?? 0,
                projectProgressService?.id ?? 0,
                formatDate(jobDate ?? null) ?? '',
                formatDate(jobSubmissionDate ?? null) ?? null,
                formatDate(applicationSubmittedDate ?? null) ?? null,
                formatDate(decisionDate ?? null) ?? null,
            );

            if (response.statusCode === 200) {
                await handleGetProjectProgressData();
                handleCloseProjectProgressModal('Close');

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Project progress updated successfully.",
                        life: 3000,
                    });
                }
            } else if (response.statusCode === 422) {
                Object.entries(response.errors ?? []).map(([key, value]) => (
                    showAPIErrorForProjectProgress(key, value[0])
                ));
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error updating the project progress.",
                        life: 3000,
                    });
                }
            }
        } catch (error) {
            console.error("Error updating the project progress:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while updating the project progress.",
                life: 3000,
            });
        } finally {
            setLoadingState(null);
        }
    }
    /* **************************************************************** */


    /* **************************************************************** Delete Project Progress */
    const handleDeleteProjectProgress = (projectProgress: ProjectProgressType) => {
        if (projectProgress) {
            const projectProgressId = projectProgress?.id;

            confirmDialog({
                message: "Are you sure you want to delete this project progress?",
                header: "Confirm the deletion",
                icon: "bi bi-info-circle",
                defaultFocus: "accept",
                acceptClassName: "p-button-danger",
                accept: () => deleteProjectProgress(projectProgressId!),
                dismissableMask: true,
                resizable: false,
                draggable: false,
            });
        }
    }

    const deleteProjectProgress = async (projectProgressId: number) => {
        if (!projectProgressId) return;

        setDeleteProgressLoadingId(projectProgressId);

        try {
            const result = await deleteProjectProgressAPI(projectProgressId);

            if (result.statusCode === 200) {
                await handleGetProjectProgressData();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Project progress deleted successfully.",
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error deleting the project progress.",
                        life: 3000,
                    });
                }
            }
        } catch (error) {
            console.error("Error deleting the project progress:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while deleting the project progress.",
                life: 3000,
            });
        } finally {
            setDeleteProgressLoadingId(null);
        }
    }
    /* **************************************************************** */


    /* **************************************************************** Project Progress Status */
    const handleGetStatusIcon = (status: "Pending" | "Validated" | "In progress" | "Submitted"): string => {
        let icon = "";

        if (status === 'Pending') {
            icon = "bi bi-clock-history text-warning";
        } else if (status === 'Validated') {
            icon = "bi bi-check-circle text-success";
        } else if (status === 'In progress') {
            icon = "bi bi-hourglass text-info";
        } else if (status === 'Submitted') {
            icon = "bi bi-send text-primary";
        } else {
            icon = ''
        }

        return icon;
    }

    const statusOptionTemplate = (option: 'Pending' | 'Validated' | 'In progress' | 'Submitted') => {
        return (
            <div className="status_disply">
                <i className={handleGetStatusIcon(option)}></i>
                <div>{option}</div>
            </div>
        );
    };


    const handleGetStatusIconDecession = (status: "Pending" | "Approved" | "Rejected"): string => {
        let icon = "";

        if (status === 'Pending') {
            icon = "bi bi-clock-history text-warning";
        } else if (status === 'Approved') {
            icon = "bi bi-check-circle text-success";
        } else if (status === 'Rejected') {
            icon = "bi bi-x-circle text-danger";
        } else {
            icon = ''
        }

        return icon;
    }

    const statusOptionTemplateDecession = (option: 'Pending' | 'Approved' | 'Rejected') => {
        return (
            <div className="status_disply">
                <i className={handleGetStatusIconDecession(option)}></i>
                <div>{option}</div>
            </div>
        );
    };

    const statusValueTemplate = (option: "Pending" | "Validated" | "In progress" | "Submitted", props: DropdownProps) => {
        const lableClass = option === 'Pending' ? 'warning' : option === 'Validated' ? 'success' : option === 'In progress' ? 'info' : 'primary'

        if (option) {
            return (
                <div className="status_disply">
                    <i className={handleGetStatusIcon(option)}></i>
                    <div className={`text_bold text-${lableClass}`}>
                        {option}
                    </div>
                </div>
            );
        }

        return <span>{props.placeholder}</span>;
    };



    const statusValueTemplateDecession = (option: "Pending" | "Approved" | "Rejected", props: DropdownProps) => {
        const lableClass = option === 'Pending' ? 'warning' : option === 'Approved' ? 'success' : option === 'Rejected' ? 'danger' : 'primary'

        if (option) {
            return (
                <div className="status_disply">
                    <i className={handleGetStatusIconDecession(option)}></i>
                    <div className={`text_bold text-${lableClass}`}>
                        {option}
                    </div>
                </div>
            );
        }

        return <span>{props.placeholder}</span>;
    };


    const handleStatusChange = (newStatus: 'Pending' | 'Validated' | 'In progress' | 'Submitted', rowData: ProjectProgressType) => {
        confirmDialog({
            message: (
                <div className="text-center">
                    <span>Are you sure you want to change the application validate status of this project progress to <b className="text-capitalize">{newStatus}</b>?</span>
                </div>
            ),
            header: "Confirm status change",
            icon: null,
            defaultFocus: "accept",
            dismissableMask: true,
            resizable: false,
            draggable: false,
            accept: () => {
                projectProgressStatusChange(rowData.id, newStatus);
            }
        });
    };


    const handleStatusChangeDecession = (newStatus: 'Pending' | 'Approved' | 'Rejected', rowData: ProjectProgressType) => {
        confirmDialog({
            message: (
                <div className="text-center">
                    <span>Are you sure you want to change the decession status of this project progress to <b className="text-capitalize">{newStatus}</b>?</span>
                </div>
            ),
            header: "Confirm status change",
            icon: null,
            defaultFocus: "accept",
            dismissableMask: true,
            resizable: false,
            draggable: false,
            accept: () => {
                projectProgressStatusChangeDecession(rowData.id, newStatus);
            }
        });
    };

    const projectProgressStatusChange = async (projectProgressId: number, status: 'Pending' | 'Validated' | 'In progress' | 'Submitted') => {
        if (!projectProgressId || !status) return;

        try {
            const response = await changeProjectProgressStatusAPI(
                projectProgressId,
                status,
                formatDate(today)
            );

            if (response.statusCode === 200) {
                await handleGetProjectProgressData();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Project progress status updated successfully.",
                    life: 3000,
                });
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Error updating project progress status.",
                    detail: "There was an error updating the project progress status.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error updating the project progress status:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while updating the project progress status.",
                life: 3000,
            });
        }
    };


    const projectProgressStatusChangeDecession = async (projectProgressId: number, status: 'Pending' | 'Approved' | 'Rejected') => {
        if (!projectProgressId || !status) return;

        try {
            const response = await changeProjectProgressStatusDecessionAPI(
                projectProgressId,
                status,
                formatDate(today)
            );

            if (response.statusCode === 200) {
                await handleGetProjectProgressData();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Project progress status updated successfully.",
                    life: 3000,
                });
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Error updating project progress status.",
                    detail: "There was an error updating the project progress status.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error updating the project progress status:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while updating the project progress status.",
                life: 3000,
            });
        }
    };



    /* **************************************************************** */


    /* **************************************************************** Project Progress Table */
    const jobNoBodyTemplate = (rowData: ProjectProgressType) => {
        return (
            <span className="text_bold">
                <span>{rowData.job_number}</span>
                <small className="table_data_sub text-capitalize">
                    {rowData.address}
                </small>
            </span>
        );
    };

    const statusBodyTemplate = (rowData: ProjectProgressType) => {
        const status = rowData.application_validated;
        const hasAccess = hasPermissionForChangeProjectProgressStatus;
        const validatedDate = rowData.application_validated_date;

        return (
            <div className="d-flex flex-column">
                <div className="flex_center">
                    <Dropdown
                        value={status}
                        options={projectProgressStatuses}
                        itemTemplate={statusOptionTemplate}
                        valueTemplate={statusValueTemplate}
                        onChange={(e) => handleStatusChange(e.value, rowData)}
                        panelClassName="table_status_dropdown"
                        placeholder="Status"
                        className="form_dropdown data_status_dropdown"
                        disabled={!hasAccess}
                        dropdownIcon={!hasAccess}
                        pt={{ input: { className: 'cursor_pointer' } }}
                    />
                </div>

                {validatedDate !== null && (
                    <small className="text-center mt-1">
                        {validatedDate}
                    </small>
                )}
            </div>
        );
    };


    // dinesh changes

    const decessionStatusBodyTemplate = (rowData: ProjectProgressType) => {
        const status = rowData.decision;
        const hasAccess = hasPermissionForChangeProjectProgressStatus;
        const decision_date = rowData.decision_date;
        const showWarning = rowData.show_warning;

        return (
            <div className="d-flex flex-column">
                <div className="flex_center">
                    <Dropdown
                        value={status}
                        options={projectProgressStatusesDecession}
                        itemTemplate={statusOptionTemplateDecession}
                        valueTemplate={statusValueTemplateDecession}
                        onChange={(e) => handleStatusChangeDecession(e.value, rowData)}
                        panelClassName="table_status_dropdown"
                        placeholder="Status"
                        className={`form_dropdown data_status_dropdown ${showWarning ? 'invalid' : ''}`}
                        disabled={!hasAccess}
                        dropdownIcon={!hasAccess}
                        pt={{ input: { className: 'cursor_pointer' } }}
                    />
                </div>

                {decision_date !== null && (
                    <small className="text-center mt-1">
                        {decision_date}
                    </small>
                )}
            </div>
        );
    };

    const tableActionMenuItems: MenuItem[] = [
        {
            items: [
                {
                    label: "View",
                    command: () => {
                        if (selectedProjectProgress !== null) {
                            handleViewProjectProgress(selectedProjectProgress);
                        }
                    },
                },

                {
                    label: "Edit",
                    command: () => {
                        if (selectedProjectProgress !== null) {
                            handleEditProjectProgress(selectedProjectProgress);
                        }
                    },
                },

                {
                    label: "Delete",
                    command: () => {
                        if (selectedProjectProgress !== null) {
                            handleDeleteProjectProgress(selectedProjectProgress);
                        }
                    },
                },
            ].filter((e) => (e.label === "Edit" && hasPermissionForEditProjectProgress) || (e.label === "Delete" && hasPermissionForDeleteProjectProgress) || (e.label === "View")),
        },
    ];

    const tableActionMenuToggle = (
        e: React.MouseEvent<HTMLButtonElement>,
        data: ProjectProgressType
    ) => {
        e.stopPropagation();
        setSelectedProjectProgress(data);

        if (openMenuId === data?.id) {
            menuRefs.current[data?.id]?.hide(e);
            setOpenMenuId(null);
        } else {
            if (openMenuId !== null && menuRefs.current[openMenuId]) {
                menuRefs.current[openMenuId]?.hide(e);
            }
            menuRefs.current[data?.id!]?.toggle(e);
            setOpenMenuId(data?.id!);
        }
    };

    const projectProgressTableActionBody = (rowData: ProjectProgressType) => {
        return (
            <React.Fragment>
                <Button
                    ref={(el) => (buttonRefs.current[rowData?.id as number] = el as HTMLButtonElement | null)}
                    key={`menu_toggle_button_${rowData?.id}`}
                    id={`menu_toggle_button_${rowData?.id}`}
                    icon="bi bi-three-dots-vertical"
                    className="is_btn action_trigger_btn p-ripple"
                    onClick={(e) => tableActionMenuToggle(e, rowData)}
                    aria-controls={`popup_menu_${rowData?.id}`}
                    aria-haspopup
                />

                <Menu
                    ref={(el) => (menuRefs.current[rowData?.id!] = el)}
                    id={`popup_menu_${rowData?.id}`}
                    key={`popup_menu_${rowData?.id}`}
                    model={tableActionMenuItems}
                    popup
                    appendTo={document.body}
                    className="service_action_menu"
                />
            </React.Fragment>
        );
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (openMenuId !== null) {
                const menuElement = menuRefs.current[openMenuId]?.getElement();
                const buttonElement = buttonRefs.current[openMenuId];

                if (
                    menuElement &&
                    !menuElement.contains(event.target as Node) &&
                    buttonElement &&
                    !buttonElement.contains(event.target as Node)
                ) {
                    const syntheticEvent = {
                        stopPropagation: () => { },
                        preventDefault: () => { },
                    } as React.SyntheticEvent;
                    menuRefs.current[openMenuId]?.hide(syntheticEvent);
                    setOpenMenuId(null);
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [openMenuId]);
    /* **************************************************************** */


    /* **************************************************************** Project Progress Modal */
    const projectProgressModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">{dataState} Project Progress</h5>
            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={() => handleCloseProjectProgressModal('Close')}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const projectProgressModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary d-none d-sm-flex"
                onClick={() => handleCloseProjectProgressModal('Close')}
            />

            {dataState === 'Add' ? (
                <>
                    <Button
                        label="Save & Next"
                        onClick={(e) => handleCreateProjectProgress(e, 'Next')}
                        className="custom_btn primary_light"
                        loading={loadingState === 'Next'}
                    />

                    <Button
                        label={`${loadingState === 'Close' ? "Processing" : "Save & Close"}`}
                        onClick={(e) => handleCreateProjectProgress(e, 'Close')}
                        className="custom_btn primary"
                        loading={loadingState === 'Close'}
                    />
                </>
            ) : (
                <Button
                    label={`${loadingState === 'Close' ? "Processing" : "Update"}`}
                    onClick={(e) => handleUpdateProjectProgress(e)}
                    loading={loadingState === 'Close'}
                    className="custom_btn primary"
                    disabled={!projectProgressUpdateHasChanges}
                />
            )}
        </div>
    );
    /* **************************************************************** */


    /* **************************************************************** View Project Progress */
    const handleViewProjectProgress = (data: ProjectProgressType) => {
        if (!data) return;

        setSelectedProjectProgress(data);
        setShowProjectProgressDetailModal(true);
    }

    const handleCloseProjectProgressDetailModal = () => {
        setShowProjectProgressDetailModal(false);
    }

    const projectProgressViewModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">Project progress details</h5>
            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseProjectProgressDetailModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const viewProjectProgressStatus = (status: 'Pending' | 'Validated' | 'In progress' | 'Submitted') => {
        let colorCode = "";
        let icon = "";

        if (status === "Validated") {
            colorCode = "#20c58e";
            icon = "bi bi-check-circle";
        } else if (status === "Pending") {
            colorCode = "#f3bc6a";
            icon = "bi bi-clock";
        } else if (status === "In progress") {
            colorCode = "#0ca3e9";
            icon = "bi bi-hourglass";
        } else if (status === "Submitted") {
            colorCode = "#0d6efd";
            icon = "bi bi-send";
        }

        return (
            <span className="priority_label auto_width" style={{ backgroundColor: colorCode }}>
                <i className={`${icon} me-2`}></i>
                {status}
            </span>
        );
    };
    /* **************************************************************** */


    /* **************************************************************** Project Progress service */
    const handleCloseProjectProgressServiceModal = () => {
        setShowProjectProgressServiceModal(false);
        setProjectProgressServiceSearchKey('');
        handleResetCustomProjectProgressServiceFields();
    }

    const handleResetCustomProjectProgressServiceFields = () => {
        setCustomProjectProgressServiceName('');
        setCustomProjectProgressServiceNameError('');
        setSelectedCustomProjectProgressService(null);
        setCustomDataState('Add');
    }

    const handleAddProjectProgressService = () => {
        setShowProjectProgressServiceModal(true);
        setCustomDataState('Add');
    };

    const handleSelectProjectProgressService = (data: ProjectProgressService) => {
        setProjectProgressService(data);

        setTimeout(() => {
            handleCloseProjectProgressServiceModal();
        }, 200);
    };

    function showAPIErrorForProjectProgressService(keyName: string, error: string): void {
        if (keyName === "service_name") {
            setCustomProjectProgressServiceNameError(error);
        }
    }

    useEffect(() => {
        checkText(customProjectProgressServiceName, false, setCustomProjectProgressServiceNameError);
    }, [customProjectProgressServiceName])

    function checkCredentialsForProjectProgressService(): boolean {
        const isValidCustomProjectProgressServiceName = checkText(customProjectProgressServiceName, true, setCustomProjectProgressServiceNameError);

        return isValidCustomProjectProgressServiceName;
    }

    // Project progress service create API
    const handleCreateCustomProjectProgressService = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForProjectProgressService()) return;

        setCustomLoading(true);

        try {
            const service_name = customProjectProgressServiceName;

            const data = await createProjectProgressServiceAPI(service_name);

            if (data.statusCode === 200) {
                await dispatch(fetchProjectProgressServiceData());
                handleResetCustomProjectProgressServiceFields();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Service added successfully.",
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value]) => (
                    showAPIErrorForProjectProgressService(key, value[0])
                ));
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error adding the service.",
                        life: 3000,
                    });
                }
            }
        } catch (error) {
            console.error("Error adding the service:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while adding the service.",
                life: 3000,
            });
        } finally {
            setCustomLoading(false);
        }
    };

    const handleEditCustomProjectProgressService = (service: ProjectProgressService) => {
        if (!service) return;

        setSelectedCustomProjectProgressService(service);
        setCustomProjectProgressServiceName(service?.service_name || '');
        setCustomDataState('Edit');
    }

    useEffect(() => {
        if (!selectedCustomProjectProgressService) return;

        if (customProjectProgressServiceName && customProjectProgressServiceName !== selectedCustomProjectProgressService?.service_name) {
            setProjectProgressServiceUpdateHasChanges(true);
        } else {
            setProjectProgressServiceUpdateHasChanges(false);
        }
    }, [selectedCustomProjectProgressService, customProjectProgressServiceName]);

    // Project progress service update API
    const handleUpdateCustomProjectProgressService = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForProjectProgressService() || selectedCustomProjectProgressService === null) return;

        setCustomLoading(true);

        try {
            const service_name = customProjectProgressServiceName;
            const id = selectedCustomProjectProgressService?.id;

            const data = await updateProjectProgressServiceAPI(id, service_name);

            if (data.statusCode === 200) {
                await dispatch(fetchProjectProgressServiceData());
                handleResetCustomProjectProgressServiceFields();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Service updated successfully.",
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) => (
                    showAPIErrorForProjectProgressService(key, value[0])
                ));
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error updating the service.",
                        life: 3000,
                    });
                }
            }
        } catch (error) {
            console.error("Error updating the service:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while updating the service.",
                life: 3000,
            });
        } finally {
            setCustomLoading(false);
        }
    };

    const handleDeleteCustomProjectProgressService = (service: ProjectProgressService) => {
        if (service) {
            handleResetCustomProjectProgressServiceFields();
            const customServiceId = service?.id;

            confirmDialog({
                message: (
                    <div className="text-center d-flex justify-content-center flex-column">
                        <span>Are you sure you want to delete this <b className="text-capitalize">{service?.service_name}</b> service?</span>
                        <span>This action will <b>delete all</b> records associated with it.</span>
                    </div>
                ),
                header: "Confirm the deletion",
                icon: "bi bi-info-circle",
                defaultFocus: "accept",
                acceptClassName: "p-button-danger",
                accept: () => deleteCustomProjectProgressService(customServiceId!),
                dismissableMask: true,
                resizable: false,
                draggable: false,
            });
        }
    }

    // Project progress service delete API
    const deleteCustomProjectProgressService = async (customServiceId: number) => {
        if (!customServiceId) return;

        setDeleteServiceLoadingId(customServiceId);

        try {
            const id = customServiceId;
            const result = await deleteProjectProgressServiceAPI(id);

            if (result.statusCode === 200) {
                await dispatch(fetchProjectProgressServiceData());
                await handleGetProjectProgressData();
                setProjectProgressService(null);

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Service deleted successfully.",
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error deleting the service.",
                        life: 3000,
                    });
                }
            }
        } catch (error) {
            console.error("Error deleting the service:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while deleting the service.",
                life: 3000,
            });
        } finally {
            setDeleteServiceLoadingId(null);
        }
    }
    /* **************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingProjectProgress && loadingProjectProgressService && initialDataLoading ? (
                    <PageLoader />
                ) : errorProjectProgress ? (
                    <ErrorPage errorMessage={errorProjectProgress} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="d-flex align-items-center justify-content-between">
                            <h4 className="main_heading">Project progress </h4>

                            <GoBackButton />
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3">
                            <div className="contact_tabs">
                                {projectProgressTabs?.map((tab) => (
                                    <TabFilter
                                        key={tab}
                                        label={tab}
                                        isActive={activeTab === tab}
                                        onTabChange={() => handleFilterProjectProgress(tab)}
                                        tabClassName={tab === 'All' ? 'primary' : tab === 'Pending' ? 'warning' : tab === 'Validated' ? 'success' : tab === 'In progress' ? 'info' : tab === 'Submitted' ? 'primary' : ''}
                                        disabled={projectProgresses?.length === 0}
                                    />
                                ))}
                            </div>

                            <div className="page_action_area">
                                {hasPermissionForAddProjectProgress && (
                                    <button
                                        className="add_data_link_btn p-ripple"
                                        onClick={handleAddProjectProgress}
                                    >
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add project progress</span>
                                        <Ripple />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="page_filter_area mt-sm-3 mt-2">
                            <div className="row px-2 px-sm-1 row-gap-2">
                                <div className="col-5 col-sm-4 col-lg-3 px-1 px-sm-2">
                                    <div className="page_filter_group has_icon">
                                        <DatePicker
                                            key={`jobDateFilter`}
                                            id="jobDateFilter"
                                            labelHtmlFor="jobDateFilter"
                                            className="as_filter"
                                            value={jobDateFilter}
                                            maxDate={today}
                                            dateFormat="dd M yy"
                                            placeholder="Filter by Job date"
                                            onChange={setJobDateFilter}
                                            showClear
                                            showIcon
                                            icon="bi bi-calendar2-minus-fill"
                                            onClear={() => setJobDateFilter(null)}
                                            formGroupClassName="mb-0"
                                        />
                                    </div>
                                </div>

                                <div className="col-7 col-sm-8 col-lg-4 px-1 px-sm-2">
                                    <div className="page_filter_group">
                                        <Dropdown
                                            key={`projectFilter`}
                                            id="projectFilter"
                                            value={projectFilter}
                                            onChange={(e: DropdownChangeEvent) => setProjectFilter(e.value)}
                                            optionLabel="address"
                                            itemTemplate={(option: Project) => {
                                                return (
                                                    <span className="text-capitalize">
                                                        <span className="text_bold">{option?.job_number}</span>:&nbsp;{option?.address}
                                                    </span>
                                                )
                                            }}
                                            valueTemplate={(option: Project, props: DropdownProps) => {
                                                if (option) {
                                                    return (
                                                        <span className="text-capitalize">
                                                            <span className="text_bold">{option?.job_number}</span>:&nbsp;{option?.address}
                                                        </span>
                                                    )
                                                }

                                                return <span>{props.placeholder}</span>;
                                            }}
                                            filter
                                            emptyFilterMessage="The project you were looking for is not found!"
                                            emptyMessage="No project(s) found!"
                                            filterBy="address,job_number"
                                            filterPlaceholder="Search by address/job no."
                                            options={projects}
                                            panelClassName="status_option_panel text-capitalize"
                                            placeholder="Filter by project"
                                            className="form_dropdown as_filter w-100 text-capitalize"
                                            loading={loadingProjects}
                                            showClear
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="page_main_content_section p-0">
                            {filteredProjectProgressData && filteredProjectProgressData?.length > 0 ? (
                                <DataTable
                                    value={filteredProjectProgressData}
                                    paginator
                                    size="small"
                                    rows={10}
                                    emptyMessage="No records found!"
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    tableStyle={{ minWidth: "50rem", maxHeight: '55vh' }}
                                    paginatorDropdownAppendTo={"self"}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    scrollable
                                    loading={loadingProjectProgress}
                                    scrollHeight="55vh"
                                >
                                    <Column
                                        header="Job no."
                                        field="job_number"
                                        headerClassName="text_purple"
                                        alignHeader={"left"}
                                        body={jobNoBodyTemplate}
                                        style={{ width: "20%" }}
                                    ></Column>

                                    <Column
                                        header="Service"
                                        headerClassName="text_purple"
                                        field="service.service_name"
                                        body={(rowData: ProjectProgressType) => (
                                            <span className="text_no_wrap text_bold">
                                                {rowData?.service ? rowData?.service?.service_name : "---"}
                                            </span>
                                        )}
                                        style={{ width: "12%" }}
                                    ></Column>

                                    <Column
                                        header="Start"
                                        headerClassName="text_purple"
                                        field="job_date"
                                        body={(rowData: ProjectProgressType) => (
                                            <span className="text_no_wrap">
                                                {rowData?.job_date ? rowData?.job_date : "---"}
                                            </span>
                                        )}
                                        style={{ width: "12%" }}
                                    ></Column>

                                    {/* <Column
                                        header="Completed"
                                        headerClassName="text_purple"
                                        field="job_submission_date"
                                        body={(rowData: ProjectProgressType) => (
                                            <span className="text_no_wrap">
                                                {rowData?.job_submission_date ? rowData?.job_submission_date : "---"}
                                            </span>
                                        )}
                                        style={{ width: "12%" }}
                                    ></Column>

                                    <Column
                                        header="Submission"
                                        headerClassName="text_purple"
                                        field="application_submitted_date"
                                        body={(rowData: ProjectProgressType) => (
                                            <span className="text_no_wrap">
                                                {rowData?.application_submitted_date ? rowData?.application_submitted_date : "---"}
                                            </span>
                                        )}
                                        style={{ width: "12%" }}
                                    ></Column> */}

                                    <Column
                                        header="Status"
                                        alignHeader={"center"}
                                        field="status"
                                        headerClassName="text_purple"
                                        body={statusBodyTemplate}
                                        style={{ width: "15%" }}
                                    ></Column>


                                    <Column
                                        header="Decision"
                                        alignHeader={"center"}
                                        field="decision"
                                        headerClassName="text_purple"
                                        body={decessionStatusBodyTemplate}
                                        style={{ width: "12%" }}
                                    ></Column>

                                    {/* <Column
                                        header="Decision"
                                        headerClassName="text_purple"
                                        field="decision_date"
                                        body={(rowData: ProjectProgressType) => (
                                            <span className="text_no_wrap">
                                                {rowData?.decision_date ? rowData?.decision_date : "---"}
                                            </span>
                                        )}
                                        style={{ width: "12%" }}
                                    ></Column> */}

                                    <Column
                                        body={projectProgressTableActionBody}
                                        style={{ width: "5%" }}
                                        frozen
                                        alignFrozen="right"
                                    ></Column>
                                </DataTable>
                            ) : (
                                <NoDataCreated
                                    message={getEmptyMessage()}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>


            {/* Project progress modal */}
            <Dialog
                visible={showProjectProgressModal}
                headerClassName="custom_modal_header"
                header={projectProgressModalHeader}
                footer={projectProgressModalFooter}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={() => handleCloseProjectProgressModal('Close')}
                dismissableMask
                closeOnEscape={false}
            >
                <div className="custom_modal_body p-2 p-sm-3">
                    <div className="row">
                        {/* Project */}
                        <div className="col-12" key={`project`}>
                            <div className="page_form_group">
                                <label htmlFor="Project" className="custom_form_label is_required">
                                    Project
                                </label>

                                <Dropdown
                                    id="project"
                                    value={project}
                                    onChange={(e: DropdownChangeEvent) => setProject(e.value)}
                                    options={projects}
                                    optionLabel="job_number"
                                    valueTemplate={selectedProjectTemplate}
                                    itemTemplate={projectOptionTemplate}
                                    emptyFilterMessage="No project(s) found!"
                                    placeholder="Select project"
                                    className="form_dropdown address_selection w-100"
                                    filter
                                    showClear
                                    filterInputAutoFocus
                                    filterBy="job_number,address"
                                    filterPlaceholder="Search by address/job no."
                                    panelClassName="address_dropdown_panel"
                                    loading={loadingProjects}
                                />

                                {projectError && (
                                    <small className="form_error_msg">{projectError}</small>
                                )}
                            </div>
                        </div>

                        {/* Service */}
                        <div className="col-12" key={`projectProgressService`}>
                            <DataListModalInput
                                key={`projectProgressService`}
                                inputId="projectProgressService"
                                label="Service"
                                value={projectProgressService}
                                displayKey="service_name"
                                onAdd={handleAddProjectProgressService}
                                required={true}
                                placeHolder="Click to select the service"
                                error={projectProgressServiceError}
                            />
                        </div>

                        {/* Start date */}
                        <div className="col-12 col-sm-6" key={`jobDate`}>
                            <DatePicker
                                key={`jobDate`}
                                id="jobDate"
                                label="Start date"
                                labelHtmlFor="jobDate"
                                required={true}
                                value={jobDate}
                                maxDate={today}
                                placeholder="dd/mm/yyyy"
                                onChange={setJobDate}
                                error={jobDateError}
                                showClear
                                onClear={() => setJobDate(null)}
                            />
                        </div>

                        {/* Completed date */}
                        <div className="col-12 col-sm-6" key={`jobSubmissionDate`}>
                            <DatePicker
                                key={`jobSubmissionDate`}
                                id="jobSubmissionDate"
                                label="Completed date"
                                labelHtmlFor="jobSubmissionDate"
                                required={false}
                                value={jobSubmissionDate}
                                placeholder="dd/mm/yyyy"
                                onChange={setJobSubmissionDate}
                                error={jobSubmissionDateError}
                                showClear
                                onClear={() => setJobSubmissionDate(null)}
                            />
                        </div>

                        {/* Submission date */}
                        <div className="col-12 col-sm-6" key={`applicationSubmittedDate`}>
                            <DatePicker
                                key={`applicationSubmittedDate`}
                                id="applicationSubmittedDate"
                                label="Submission date"
                                labelHtmlFor="applicationSubmittedDate"
                                required={false}
                                value={applicationSubmittedDate}
                                placeholder="dd/mm/yyyy"
                                onChange={setApplicationSubmittedDate}
                                error={applicationSubmittedDateError}
                                showClear
                                onClear={() => setApplicationSubmittedDate(null)}
                                formGroupClassName="mb-sm-1"
                            />
                        </div>

                        {/* Decision date */}
                        <div className="col-12 col-sm-6" key={`decisionDate`}>
                            <DatePicker
                                key={`decisionDate`}
                                id="decisionDate"
                                label="Decision date"
                                labelHtmlFor="decisionDate"
                                required={false}
                                value={decisionDate}
                                placeholder="dd/mm/yyyy"
                                onChange={setDecisionDate}
                                error={decisionDateError}
                                showClear
                                onClear={() => setDecisionDate(null)}
                                formGroupClassName="mb-1"
                            />
                        </div>
                    </div>
                </div>
            </Dialog>


            {/* Project progress service modal */}
            <Dialog
                visible={showProjectProgressServiceModal}
                headerClassName="d-none"
                dismissableMask
                className="custom_modal_dialog modal_dialog_sm data_select_modal"
                contentClassName="data_select_modal_content"
                onHide={handleCloseProjectProgressServiceModal}
            >
                <div className="custom_modal_body data_select_modal_area">
                    <div className="data_select_modal_header">
                        <DataListModalSearch
                            key={`projectProgressServiceSearchKey`}
                            inputId="projectProgressServiceSearchKey"
                            value={projectProgressServiceSearchKey}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectProgressServiceSearchKey(e.target.value)}
                            placeholder="Search service by name"
                            disabled={projectProgressServices?.length === 0}
                            onClear={() => setProjectProgressServiceSearchKey("")}
                        />

                        <DataListModalClose
                            onClose={handleCloseProjectProgressServiceModal}
                        />
                    </div>

                    {loadingProjectProgressService ? (
                        <CustomTableLoader />
                    ) : errorProjectProgressService ? (
                        <div className="data_select_modal_body has_custom_input">
                            <DataException
                                message="Failed to fetch project progress service data. Please try again later."
                            />
                        </div>
                    ) : (
                        <div className="data_select_modal_body has_custom_input">
                            {filteredProjectProgressServiceData && filteredProjectProgressServiceData?.length > 0 ? (
                                <div className="data_select_option_area">
                                    {filteredProjectProgressServiceData?.map((service) => (
                                        <div
                                            key={`service_${service?.id}`}
                                            className={`data_select_option p-ripple ${service?.id === projectProgressService?.id ? "selected" : ""}
                                        ${service?.type === 'Custom' ? 'custom' : ''}`}
                                            onClick={() => handleSelectProjectProgressService(service)}
                                        >
                                            <span>{service?.service_name}</span>
                                            <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />

                                            {service?.type === 'Custom' && (
                                                <div className="custom_data_action_area">
                                                    {hasPermissionForEditProjectProgressService && (
                                                        <button className="is_btn data_select_option_edit_btn p-ripple"
                                                            onClick={(e) => {
                                                                e.stopPropagation();

                                                                if (selectedCustomProjectProgressService !== null) {
                                                                    handleResetCustomProjectProgressServiceFields();
                                                                } else {
                                                                    handleEditCustomProjectProgressService(service);
                                                                }
                                                            }}>
                                                            <i className={`bi ${(selectedCustomProjectProgressService !== null && selectedCustomProjectProgressService?.id === service?.id) ? 'bi-x-lg' : 'bi-pencil-square'}`}></i>
                                                        </button>
                                                    )}

                                                    {hasPermissionForDeleteProjectProgressService && (
                                                        <button className="is_btn data_select_option_edit_btn delete p-ripple"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteCustomProjectProgressService(service);
                                                            }}
                                                            disabled={deleteServiceLoadingId === service?.id}
                                                        >
                                                            <i className={deleteServiceLoadingId === service?.id ? 'ri-loader-4-fill loading_spinner' : 'bi bi-trash'}></i>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <DataException
                                    message={projectProgressServiceSearchKey ? 'The service you were looking for is not found!' :
                                        `No services found. ${hasPermissionForAddProjectProgressService ? 'To add the service click on add button' : ''}`}
                                />
                            )}
                        </div>
                    )}

                    {hasPermissionForAddProjectProgressService && (
                        <div className="page_form_group data_select_modal_footer mb-0">
                            <div className="input_with_button_area">
                                <InputText
                                    key={`customProjectProgressServiceName`}
                                    id="customProjectProgressServiceName"
                                    value={customProjectProgressServiceName}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomProjectProgressServiceName(e.target.value)}
                                    className="custom_form_input has_add_value custom_data_input"
                                    placeholder="Enter service name"
                                    autoComplete="off"
                                />
                                <button
                                    className="add_value_btn fill p-ripple"
                                    onClick={customDataState === 'Add' ? handleCreateCustomProjectProgressService : handleUpdateCustomProjectProgressService}
                                    disabled={(customLoading || customProjectProgressServiceName === "") || (customDataState === 'Edit' && !projectProgressServiceUpdateHasChanges)}
                                >
                                    <i className={customLoading ? "ri-loader-4-fill loading_spinner" : customDataState === 'Edit' ? "bi bi-pencil-square" : "bi bi-plus-lg"}></i>
                                    <span>{customLoading ? "Processing" : customDataState === 'Edit' ? "Update" : "Add"}</span>
                                    <Ripple />
                                </button>
                            </div>
                            {customProjectProgressServiceNameError && (
                                <small className="form_error_msg">
                                    {customProjectProgressServiceNameError}
                                </small>
                            )}
                        </div>
                    )}
                </div>
            </Dialog>


            {/* Project Progress Detail Modal */}
            <Dialog
                visible={showProjectProgressDetailModal}
                header={projectProgressViewModalHeader}
                headerClassName="custom_modal_header"
                className={`custom_modal_dialog modal_dialog_sm`}
                onHide={handleCloseProjectProgressDetailModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1">
                        {/* Project */}
                        <div className="row">
                            <div className="col-5">
                                <h6 className="data_view_title">Project:</h6>
                            </div>
                            <div className="col-7">
                                <p className="data_view_content text_purple">
                                    {selectedProjectProgress?.job_number}:{" "} {selectedProjectProgress?.address}
                                </p>
                            </div>
                        </div>

                        <hr className="custom_horizontal_line" />

                        {/* Service */}
                        <div className="row">
                            <div className="col-5">
                                <h6 className="data_view_title">Service:</h6>
                            </div>
                            <div className="col-7">
                                <p className="data_view_content text-dark text_bold">
                                    {selectedProjectProgress?.service?.service_name ?? '---'}
                                </p>
                            </div>
                        </div>

                        <hr className="custom_horizontal_line" />

                        {/* Start Date */}
                        <div className="row">
                            <div className="col-5">
                                <h6 className="data_view_title">Start date:</h6>
                            </div>
                            <div className="col-7">
                                <p className="data_view_content">
                                    {selectedProjectProgress?.job_date ?? "---"}
                                </p>
                            </div>
                        </div>

                        <hr className="custom_horizontal_line" />

                        {/* Completed Date */}
                        <div className="row">
                            <div className="col-5">
                                <h6 className="data_view_title">Completed date:</h6>
                            </div>
                            <div className="col-7">
                                <p className="data_view_content">
                                    {selectedProjectProgress?.job_submission_date ?? "---"}
                                </p>
                            </div>
                        </div>

                        <hr className="custom_horizontal_line" />

                        {/* Submission Date */}
                        <div className="row">
                            <div className="col-5">
                                <h6 className="data_view_title">Submission date:</h6>
                            </div>
                            <div className="col-7">
                                <p className="data_view_content">
                                    {selectedProjectProgress?.application_submitted_date ?? "---"}
                                </p>
                            </div>
                        </div>

                        <hr className="custom_horizontal_line" />

                        {/* Application Validated Status */}
                        <div className="row">
                            <div className="col-5">
                                <h6 className="data_view_title">Application validated:</h6>
                            </div>
                            <div className="col-7">
                                {viewProjectProgressStatus(selectedProjectProgress?.application_validated!)}
                            </div>
                        </div>

                        {selectedProjectProgress?.application_validated === 'Validated' && (
                            <>
                                <hr className="custom_horizontal_line" />

                                {/* Validated Date */}
                                <div className="row">
                                    <div className="col-5">
                                        <h6 className="data_view_title">Validated date:</h6>
                                    </div>
                                    <div className="col-7">
                                        <p className="data_view_content">
                                            {selectedProjectProgress?.application_validated_date ?? "---"}
                                        </p>
                                    </div>
                                </div>
                            </>
                        )}

                        <hr className="custom_horizontal_line" />

                        {/* Decision Date */}
                        <div className="row">
                            <div className="col-5">
                                <h6 className="data_view_title">Decision date:</h6>
                            </div>
                            <div className="col-7">
                                <p className="data_view_content">
                                    {selectedProjectProgress?.decision_date ?? "---"}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </React.Fragment>
    );
};

export default ProjectProgress;